import AiIcon from "../../Core/AiIcon/AiIcon";
import React, { useCallback, useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAiAssistantStore } from "../../../hooks/aiAssistant.hooks";
import { AiApprovalDecision } from "../../../models/AiAssistant";
import { ApiErrorResponse } from "../../../models/ApiResponse";
import {
	useAiConversation,
	useAiConversations,
	useAiTurnEvents,
	useCreateAiConversation,
	useRespondToAiApprovals,
	useSendAiMessage,
} from "../../../networking/ai-assistant.hooks";
import AiConversation from "./AiConversation";
import AiConversationList from "./AiConversationList";
import AiPromptInput from "./AiPromptInput";
import { isResumable, resolveActiveConversation } from "./resolveActiveConversation";

export const AI_ASSISTANT_SIDEBAR_WIDTH = 440;

interface AiAssistantSidebarProps {
	className?: string;
}

/**
 * The docked assistant panel rendered as a flex sibling of the page in
 * `LoggedInLayout` — not an overlay, so the page stays usable and links in
 * the answers can be followed while the thread stays open.
 *
 * Transport is the detached POST contract of oo-integration-service: a
 * message or approval is accepted with 202 and the conversation flips to
 * `status: "running"`; the assistant turn lands on the server and is picked
 * up by polling plus a per-user SSE event. If the assistant proposes writes
 * the finished conversation carries `pendingApprovals`, rendered as one card
 * that must be answered before the assistant continues.
 *
 * Which conversation is shown is resolved from the server list rather than
 * remembered: the newest thread is picked up again only while it is still
 * current (see `resolveActiveConversation`), otherwise the panel starts
 * fresh with the previous thread one click away. A conversation the user
 * picked themselves is never swapped out under them.
 */
const AiAssistantSidebar: React.FC<AiAssistantSidebarProps> = ({ className }) => {
	const {
		isOpen,
		close,
		activeConversationId,
		isExplicitSelection,
		selectConversation,
		resumeConversation,
	} = useAiAssistantStore();
	const { conversations, isFetched: isListFetched } = useAiConversations(isOpen);
	const { conversation, error: loadError } = useAiConversation(activeConversationId);
	const createConversation = useCreateAiConversation();
	const sendMessage = useSendAiMessage();
	const respondToApprovals = useRespondToAiApprovals();

	const [lastText, setLastText] = useState<string>();
	const [error, setError] = useState<string>();

	useAiTurnEvents(isOpen);

	// Re-evaluate staleness when the tab comes back into view, so a panel
	// left open overnight shows the empty state before the user types.
	const [focusTick, setFocusTick] = useState(0);
	useEffect(() => {
		if (!isOpen) return;
		const bump = () => {
			if (document.visibilityState === "visible") setFocusTick((tick) => tick + 1);
		};
		window.addEventListener("focus", bump);
		document.addEventListener("visibilitychange", bump);
		return () => {
			window.removeEventListener("focus", bump);
			document.removeEventListener("visibilitychange", bump);
		};
	}, [isOpen]);

	// Pick the thread to show whenever the panel opens, the list changes or
	// the tab regains focus. Explicit selections are left alone.
	useEffect(() => {
		if (!isOpen || !isListFetched || isExplicitSelection) return;
		const resolvedId = resolveActiveConversation(conversations)?.id;
		if (resolvedId !== activeConversationId) resumeConversation(resolvedId);
		// focusTick is only here to re-run the check with a fresh "now".
	}, [
		isOpen,
		isListFetched,
		isExplicitSelection,
		conversations,
		activeConversationId,
		resumeConversation,
		focusTick,
	]);

	// The conversation is gone (TTL, or another device cleaned up); fall
	// back to a fresh one instead of showing a dead thread.
	useEffect(() => {
		if (loadError && (loadError as ApiErrorResponse).status === 404) {
			selectConversation(undefined);
		}
	}, [loadError, selectConversation]);

	const isRunning = !!activeConversationId && conversation?.status === "running";
	const isBusy =
		createConversation.isLoading ||
		sendMessage.isLoading ||
		respondToApprovals.isLoading ||
		isRunning;

	const handleSend = useCallback(
		async (text: string) => {
			setError(undefined);
			setLastText(text);
			try {
				let conversationId = activeConversationId;
				// A thread the panel resumed on its own may have gone stale while
				// the panel stayed open; the message then starts a new one.
				if (conversationId && !isExplicitSelection) {
					const current =
						conversations.find((candidate) => candidate.id === conversationId) ?? conversation;
					if (current && !isResumable(current, Date.now())) conversationId = undefined;
				}
				if (!conversationId) {
					const created = await createConversation.mutateAsync({ title: text.slice(0, 60) });
					conversationId = created.id;
					selectConversation(conversationId);
				}
				await sendMessage.mutateAsync({ conversationId, text });
			} catch (e) {
				setError(describeError(e));
			}
		},
		[
			activeConversationId,
			isExplicitSelection,
			conversations,
			conversation,
			createConversation,
			sendMessage,
			selectConversation,
		]
	);

	const handleApprovals = useCallback(
		async (decisions: AiApprovalDecision[]) => {
			if (!activeConversationId) return;
			setError(undefined);
			setLastText(undefined);
			try {
				await respondToApprovals.mutateAsync({ conversationId: activeConversationId, decisions });
			} catch (e) {
				setError(describeError(e));
			}
		},
		[activeConversationId, respondToApprovals]
	);

	const startNew = () => {
		setError(undefined);
		selectConversation(undefined);
	};

	if (!isOpen) return null;

	// Newest thread, offered as "Fortsätt senaste" while the panel is fresh.
	const previousConversation = activeConversationId ? undefined : conversations[0];

	return (
		<aside
			className={cn("flex h-full shrink-0 flex-col border-l border-border bg-white", className)}
			style={{ width: AI_ASSISTANT_SIDEBAR_WIDTH }}
			aria-label="AI-assistent"
		>
			<header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-3">
				<AiIcon className="h-4 w-4 text-brand" />
				<h2 className="flex-1 text-sm font-semibold text-foreground">AI-assistent</h2>
				<span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
					Beta
				</span>
				<AiConversationList
					conversations={conversations}
					activeConversationId={activeConversationId}
					onSelect={(conversationId) => {
						setError(undefined);
						selectConversation(conversationId);
					}}
				/>
				<button
					type="button"
					onClick={startNew}
					title="Ny konversation"
					className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-neutral-100 hover:text-foreground"
				>
					<Plus className="h-4 w-4" />
				</button>
				<button
					type="button"
					onClick={close}
					title="Stäng (⌘J)"
					className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-neutral-100 hover:text-foreground"
				>
					<X className="h-4 w-4" />
				</button>
			</header>

			<div className="min-h-0 flex-1 overflow-y-auto">
				<AiConversation
					conversation={activeConversationId ? conversation : undefined}
					isResolving={!isListFetched}
					previousConversation={previousConversation}
					onResume={(conversationId) => {
						setError(undefined);
						selectConversation(conversationId);
					}}
					isThinking={isBusy}
					isApproving={respondToApprovals.isLoading}
					error={error}
					onRetry={lastText ? () => handleSend(lastText) : undefined}
					onSuggestion={handleSend}
					onRespondToApprovals={handleApprovals}
				/>
			</div>

			<footer className="shrink-0 border-t border-border bg-neutral-50 p-3">
				<AiPromptInput onSend={handleSend} isLoading={isBusy} autoFocus />
				<p className="mt-1.5 text-center text-[11px] text-foreground-subtle">
					Svaren bygger på din behörighet. Ändringar görs först när du godkänner dem.
				</p>
			</footer>
		</aside>
	);
};

function describeError(e: unknown): string {
	const apiError = e as Partial<ApiErrorResponse> | undefined;
	const code = apiError?.error?.code;
	if (code === "AI_NOT_ENABLED") return "AI-assistenten är inte aktiverad för din organisation.";
	if (code === "AI_NOT_CONFIGURED") return "AI-assistenten är inte konfigurerad på servern.";
	if (code === "PERMISSION_DENIED") return "Du saknar behörighet att använda AI-assistenten.";
	if (code === "AI_TURN_IN_PROGRESS")
		return "Assistenten arbetar fortfarande med föregående svar. Vänta tills det är klart.";
	return apiError?.error?.detail || apiError?.error?.title || "Något gick fel. Försök igen.";
}

export default AiAssistantSidebar;
